import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IhLabResult } from '../../../../models/IhLabResult.model';
import { Sample } from '../../../../models/sample.model';
import { UnitSymbols } from '../../../../enums/units.enum';
import { TenantRiskService } from '../../../../services/tenantRisk.service';
import { JurisdictionRiskConfigService } from '../../../../services/Jurisdiction-risk-config.service';
import { TranslateService } from '../../../../services/translate.service';
import { GravimetricPresentationService, GravimetricPresentationLabels } from './gravimetric-presentation.service';

export interface SampleGravimetricData {
  sample: Sample;
}

export enum GravimetricCardTemplate {
  Default = 'default',
  Compact = 'compact'
}

export interface RiskLevel {
  Name: string;
  QuantThreshold_OEL: [number, number];
  MinSamples: number;
  Color: string;
}

export interface JurisdictionData {
  ihLabResult: IhLabResult;
  exposurePercentage: number;
  rawDosePercentage: number | null;
  shiftAdjustedDosePercentage: number | null;
  exposureColor: string;
  showCensorSymbol: boolean;
  censorSymbol: string;
  progressWidth: number;
  oelPosition: number;
  isLoq: boolean;
  oelLabel: string;
  convertedTwa: number;
  convertedOel: number;
  effectiveOelValue: number;
}

export interface GroupedAgentData {
  agentId: string;
  agentName: string;
  agentUnits: number | string;
  jurisdictions: JurisdictionData[];
  maxExposurePercentage: number;
  maxExposureColor: string;
}

export interface JurisdictionGroup {
  jurisdictionId: string | undefined;
  jurisdictionName: string;
  agents: GroupedAgentData[];
  legendItems: { color: string; text: string }[];
}

@Component({
    standalone: true,
    selector: 'app-gravimetric-widget',
    imports: [CommonModule],
    templateUrl: './gravimetric-widget.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './gravimetric-widget.component.scss'
})
export class GravimetricWidgetComponent implements OnInit, OnChanges {
  @Input() data: SampleGravimetricData | null = null;
  @Input() showHeader: boolean = true;
  @Input() cardTemplate: GravimetricCardTemplate = GravimetricCardTemplate.Default;
  @Input() riskLevels: RiskLevel[] = [];
  /** Pre-loaded jurisdiction risk levels from report context (avoids async fetch) */
  @Input() jurisdictionRiskLevelsInput: Record<string, RiskLevel[]> = {};
  @Input() isVoid: boolean = false;
  @Input() voidReason: string = '';

  /** Fallback tenant risk levels */
  private tenantRiskLevels: RiskLevel[] = [];
  /** Jurisdiction-specific risk levels: jurisdictionId -> RiskLevel[] */
  private jurisdictionRiskLevelsMap: Map<string, RiskLevel[]> = new Map();

  groupedAgents: GroupedAgentData[] = [];
  legendItems: { color: string; text: string }[] = [];
  /** Jurisdiction groups — each jurisdiction in its own box with its own legend */
  jurisdictionGroups: JurisdictionGroup[] = [];
  isNoiseSample: boolean = false;
  duration: string = '';

  constructor(
    private presentationService: GravimetricPresentationService,
    private tenantRiskService: TenantRiskService,
    private jurisdictionRiskConfigService: JurisdictionRiskConfigService,
    private translateService: TranslateService
  ) { }

  ngOnInit() {
    if (!this.riskLevels.length) {
      this.loadRiskLevels();
    } else {
      this.tenantRiskLevels = this.riskLevels;
      this.buildLegendItems();

      if (this.jurisdictionRiskLevelsInput && Object.keys(this.jurisdictionRiskLevelsInput).length) {
        Object.entries(this.jurisdictionRiskLevelsInput).forEach(([jId, levels]) => {
          this.jurisdictionRiskLevelsMap.set(jId, levels as RiskLevel[]);
        });
        this.processData();
      } else {
        this.processData();
        this.loadJurisdictionRiskConfigs();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['riskLevels'] && this.riskLevels.length) {
      this.tenantRiskLevels = this.riskLevels;
      this.buildLegendItems();
    }
    if (changes['data']) {
      this.processData();
      if (!this.jurisdictionRiskLevelsInput || !Object.keys(this.jurisdictionRiskLevelsInput).length) {
        this.loadJurisdictionRiskConfigs();
      }
    }
  }

  private loadRiskLevels(): void {
    this.tenantRiskService.getCurrent().subscribe({
      next: (result) => {
        try {
          this.tenantRiskLevels = result?.exposureLikelihoodLevels
            ? this.parseRiskLevelsFromJson(result.exposureLikelihoodLevels)
            : [];
          this.riskLevels = this.tenantRiskLevels;
        } catch {
          this.tenantRiskLevels = [];
          this.riskLevels = [];
        }
        this.buildLegendItems();
        this.loadJurisdictionRiskConfigs();
      },
      error: () => {
        this.loadJurisdictionRiskConfigs();
      }
    });
  }

  /** Load jurisdiction-specific risk configs for all unique jurisdictions in results */
  private loadJurisdictionRiskConfigs(): void {
    const ihLabResults = this.data?.sample?.ihLabResults;
    if (!ihLabResults?.length) {
      this.processData();
      return;
    }

    const uniqueJurisdictionIds = [...new Set(
      ihLabResults
        .map(r => r.jurisdictionId)
        .filter(id => id != null)
    )];

    if (!uniqueJurisdictionIds.length) {
      this.processData();
      return;
    }

    this.jurisdictionRiskConfigService.getByJurisdictionIds(uniqueJurisdictionIds).subscribe({
      next: (configs) => {
        configs.forEach(config => {
          if (config?.exposureLikelihoodLevels) {
            try {
              const levels = this.parseRiskLevelsFromJson(config.exposureLikelihoodLevels);
              this.jurisdictionRiskLevelsMap.set(config.jurisdictionId, levels);
            } catch {
            }
          }
        });
        this.processData();
      },
      error: () => {
        this.processData();
      }
    });
  }

  private buildLegendItems(): void {
    this.legendItems = this.buildLegendFromRiskLevels(this.tenantRiskLevels);
  }

  private parseRiskLevelsFromJson(exposureLikelihoodLevelsJson: string): RiskLevel[] {
    const parsed = JSON.parse(exposureLikelihoodLevelsJson) as Record<string, unknown>;
    return Object.entries(parsed)
      .filter((entry): entry is [string, RiskLevel] => {
        const [key, value] = entry;
        if (key === 'ConsequenceRatings') return false;
        return typeof value === 'object' && value !== null && !Array.isArray(value);
      })
      .map(([_, value]) => value);
  }

  private buildLegendFromRiskLevels(riskLevels: RiskLevel[]): { color: string; text: string }[] {
    return riskLevels.map((level, index) => {
      const [start, end] = level.QuantThreshold_OEL;
      let text: string;
      if (start === 0) {
        text = `<${end + 0.01}%`;
      } else if (index === riskLevels.length - 1) {
        text = `>${start}%`;
      } else {
        text = `${start}-${end}%`;
      }
      return { color: level.Color, text };
    });
  }

  private buildPresentationLabels(): GravimetricPresentationLabels {
    return {
      duration: this.durationLabel,
      analyte: this.analyteLabel,
      dosePercent: this.dosePercentLabel,
      shiftAdjustedDosePercent: this.shiftAdjustedDosePercentLabel,
      percentageOfOel: this.percentageOfOelLabel,
      dbaTwa: this.dbaTwaLabel,
      jurisdiction: this.jurisdictionLabel,
      oel: this.oelLabel,
      dba: this.dbaLabel,
      riskLegendDose: this.riskLegendDoseLabel,
      riskLegendOel: this.riskLegendOelLabel,
      loqDisplay: this.loqDisplayText,
      shiftAdjustedLabel: this.shiftAdjustedLabel
    };
  }

  private processData(): void {
    const presentation = this.presentationService.build(
      this.data?.sample,
      this.tenantRiskLevels,
      this.jurisdictionRiskLevelsMap,
      this.buildPresentationLabels()
    );

    this.isNoiseSample = presentation.isNoiseSample;
    this.duration = presentation.duration;
    this.jurisdictionGroups = presentation.groups;
    this.groupedAgents = presentation.groups.flatMap(group => group.agents);
  }

  getUnitSymbol(unitValue: number | string): string {
    const unitNum = typeof unitValue === 'string' ? parseInt(unitValue, 10) : unitValue;
    return (unitNum !== undefined && unitNum !== null && !isNaN(unitNum)) ? UnitSymbols[unitNum] || '' : '';
  }

  getExposureLevel(percentage: number): string {
    if (percentage >= 100) return 'high';
    if (percentage >= 75) return 'medium';
    return 'low';
  }

  get durationLabel(): string {
    return this.translateService.getDynamicTranslation('Duration');
  }

  get analyteLabel(): string {
    return this.translateService.getDynamicTranslation('Analyte');
  }

  get dosePercentLabel(): string {
    return this.translateService.getDynamicTranslation('Dose %');
  }

  get shiftAdjustedDosePercentLabel(): string {
    return this.translateService.getDynamicTranslation('Shift Adjusted Dose %');
  }

  get percentageOfOelLabel(): string {
    return this.translateService.getDynamicTranslation('Percentage of OEL');
  }

  get dbaTwaLabel(): string {
    return this.translateService.getDynamicTranslation('dBA (TWA)');
  }

  get jurisdictionLabel(): string {
    return this.translateService.getDynamicTranslation('Jurisdiction');
  }

  get oelLabel(): string {
    return this.translateService.getDynamicTranslation('OEL');
  }

  get shiftAdjustedLabel(): string {
    return this.translateService.getDynamicTranslation('(Shift Adjusted)');
  }

  get dbaLabel(): string {
    return this.translateService.getDynamicTranslation('dBA');
  }

  get riskLegendDoseLabel(): string {
    return this.translateService.getDynamicTranslation('Risk Legend as % of Dose:');
  }

  get riskLegendOelLabel(): string {
    return this.translateService.getDynamicTranslation('Risk Legend as % of Occupational Exposure Limit (OEL):');
  }

  get noNoiseResultsLabel(): string {
    return this.translateService.getDynamicTranslation('No Noise Results Available');
  }

  get noLabResultLabel(): string {
    return this.translateService.getDynamicTranslation('No Lab Result Available');
  }

  get loqDisplayText(): string {
    return this.translateService.getDynamicTranslation('< Limit of Quantification');
  }

  get sampleVoidedLabel(): string {
    return this.translateService.getDynamicTranslation('Sample Voided');
  }

  get voidReasonLabel(): string {
    return this.translateService.getDynamicTranslation('Void Reason');
  }

    public trackByIndex = trackByIndex;
}
